// src/pages/budgets.ts —— 06 C-27 预算池（D31 共享虚拟卡：预算池 + 卡限额）、C-30 上游成本台账（D33）、C-25 金库归集登记（D24）。
// 数据：GET /api/admin/budgets（+ /{id}/ops）、/upstream-costs、/treasury/sweeps；写端点见 06 C-27「API 汇总」与 C-25 表单规格。
// 每个 await 之后先过 sessionGuard（会话已换则不提示、不刷新、不写抽屉）；抽屉回填以对象同一性判定仍是本次打开的抽屉
import { ApiError } from '../../../shared/src/api/client';
import type { RouteKey } from '../../../shared/src/api/routes';
import type { AdminPage } from '../../../shared/src/api/types';
import type { Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { cmp, fmtAmount, parseDecimalInput, toMicro, type Micro } from '../../../shared/src/money';
import { fmtDateTime } from '../../../shared/src/time';
import { closeDrawer, openConfirm, openDrawer, openForm, reloadCurrent, sessionGuard } from '../domains/table';
import { str, type AdminCtx } from '../domains/types';
import { I18N_BUDGETS, REWRITES_BUDGETS } from '../i18n/budgets';
import { can } from '../perm';
import type { BudgetOp, BudgetRow, Sweep, SyncResult, TreasurySummary, UpstreamCost, WalletCoverage } from './budgets.types';
import { openCardDrawer } from './cards';
import { chipMap, defineView, type DrawerAction, type DrawerSpec, type FormDef, type Need, type PageMap, type StatVals } from './types';
import { cell, chipOf, localErr, type CellVals, type ChipKind, type Tone } from './ui';

const W_EDIT: Need = { module: 'wallets', bit: 'edit' };
const W_APPROVE: Need = { module: 'wallets', bit: 'approve' };
const RED = 'color:#d1434b';
const GRAY = 'color:#8a958f';

const TOPUP_ROUTE = 'POST /api/admin/budgets/{id}/topup';
const WITHDRAW_ROUTE = 'POST /api/admin/budgets/{id}/withdraw';
const RESOLVE_ROUTE: RouteKey = 'POST /api/admin/budgets/ops/{biz_no}/resolve';

// ---- 格式化（D8-12 截断展示；后端非法值原样显示，不抛错） ----

function micro(v: string | null | undefined): Micro | null {
  if (typeof v !== 'string') return null;
  try {
    return toMicro(v);
  } catch {
    return null;
  }
}

function money(v: string, dp = 2): string {
  try {
    return fmtAmount(v, dp);
  } catch {
    return v;
  }
}

const usd = (v: string): string => `$${money(v)}`;
// 有符号金额（F-14c 净入金可为负）：负号前置于 $（与 agents 页同口径）
const signedUsd = (v: string): string => (v.trim().startsWith('-') ? `−$${money(v.trim().slice(1))}` : usd(v));
const tz = (ctx: AdminCtx): string | undefined => ctx.s.sh.timezone || undefined;
const time = (ctx: AdminCtx, iso: string | null | undefined): string => fmtDateTime(iso, { tz: tz(ctx) });
const text = (x: string | boolean | undefined): string => (typeof x === 'string' ? x.trim() : '');

// 金额输入：非负十进制且小数位 ≤ dp；返回规范化串（去首尾空白），否则 null
function norm(x: string | boolean | undefined, dp: number): string | null {
  const s = text(x);
  return new RegExp(`^\\d+(\\.\\d{1,${dp}})?$`).test(s) ? parseDecimalInput(s, dp) : null;
}

// 06 C-25 列表：地址 mono「前4…后4」、TxHash「前6…后4」（抽屉显示全文）
function abbr(s: string, head: number, tail: number): string {
  return s.length > head + tail + 1 ? `${s.slice(0, head)}…${s.slice(-tail)}` : s;
}

// 06 C-25 备注列「文本（省略）」：按码点截断，全文见抽屉
function clip(s: string, n: number): string {
  const cps = [...s];
  return cps.length > n ? `${cps.slice(0, n).join('')}…` : s;
}

// ---- 状态映射 ----

const BUDGET_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = { active: ['green', 'stActive'], deleted: ['red', 'stBudgetDeleted'] };
// 06 C-27 预算单据状态：submitted gray / sent blue / upstream_accepted blue / unknown amber / success green / failed red
const OP_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = {
  submitted: ['gray', 'bgtOpSubmitted'], sent: ['blue', 'bgtOpSent'], upstream_accepted: ['blue', 'bgtOpAccepted'],
  unknown: ['amber', 'bgtOpUnknown'], success: ['green', 'stSuccess'], failed: ['red', 'stFailed'],
};
const OP_KIND: Readonly<Record<string, string>> = { create: 'bgtOpCreate', topup: 'bgtOpTopup', withdraw: 'bgtOpWithdraw' };
// 06 C-30 类型九类：后三类（后台加款 / 人工调增 / 人工调减）amber
const COST_TYPE: Readonly<Record<string, readonly [ChipKind, string]>> = {
  budget_recharge: ['green', 'bgtCostTypeBudgetRecharge'], budget_recharge_fee: ['gray', 'bgtCostTypeBudgetRechargeFee'], budget_deduct: ['blue', 'bgtCostTypeBudgetDeduct'],
  budget_deduct_fee: ['gray', 'bgtCostTypeBudgetDeductFee'], share_open_card_fee: ['purple', 'bgtCostTypeOpenCardFee'], share_remove_card_fee: ['purple', 'bgtCostTypeRemoveCardFee'],
  admin_add: ['amber', 'bgtCostTypeAdminAdd'], manual_adjustment_in: ['amber', 'bgtCostTypeManualIn'], manual_adjustment_out: ['amber', 'bgtCostTypeManualOut'],
};

// facet 选项文案：已映射值取文案，未知值显示原值（P-12）
const facetOf =
  (entries: Readonly<Record<string, readonly [ChipKind, string]>>) =>
  (t: Dict, value: string): string => {
    const key = entries[value]?.[1];
    return (key && str(t, key)) || value;
  };

// ---- 覆盖率五卡（06 C-27 F-14a，C-30 同） ----

function isWallet(x: unknown): x is WalletCoverage {
  return typeof x === 'object' && x !== null && 'budget_balance' in x && 'live_available' in x && 'inflight_amount' in x && 'coverage' in x;
}

// coverage 为 4 位小数比值，展示为百分比（2 位，截断）；非法值原样显示
function pct(v: string): string {
  const m = micro(v);
  return m === null ? v : `${fmtAmount(m * 100n, 2)}%`;
}

function coverageStats(ctx: AdminCtx, page: Readonly<Record<string, unknown>>): readonly StatVals[] {
  const t = ctx.t;
  const w = page['wallet'];
  if (!isWallet(w)) return [];
  const c = micro(w.coverage);
  const low = c !== null && cmp(c, toMicro('1')) < 0;
  return [
    { label: str(t, 'bgtKUsdBalance'), value: w.usd_balance === null ? '—' : usd(w.usd_balance), sub: time(ctx, w.snapshot_at), dark: true },
    { label: str(t, 'bgtKBudgetBalance'), value: usd(w.budget_balance), sub: '' },
    { label: str(t, 'bgtKLiveAvailable'), value: usd(w.live_available), sub: '' },
    { label: str(t, 'bgtKInflight'), value: usd(w.inflight_amount), sub: '' },
    { label: str(t, 'bgtKCoverage'), value: w.coverage === null ? '—' : pct(w.coverage), sub: low ? str(t, 'bgtCoverageLow') : '', danger: low },
  ];
}

// ---- C-27 表单 ----

function amountForm(t: Dict, kind: 'topup' | 'withdraw', b: BudgetRow): FormDef {
  const route = kind === 'topup' ? TOPUP_ROUTE : WITHDRAW_ROUTE;
  const label = str(t, kind === 'topup' ? 'bgtTopup' : 'bgtWithdraw');
  return {
    title: `${label} · ${b.name}`,
    tip: str(t, kind === 'topup' ? 'bgtTopupTip' : 'bgtWithdrawTip'),
    fields: [{ k: 'amount', label: `${str(t, 'cAmount')} (USD)`, type: 'number' }],
    init: { amount: '' },
    // 06：两位小数且 ≥ 1（配置下限更高时由服务端 4220 field=amount 拦截）；转出超余量 → 1207 reason=budget_surplus（ui.err 括注 reason）
    validate: (v) => {
      const a = norm(v['amount'], 2);
      return a === null || cmp(toMicro(a), toMicro('1')) < 0 ? str(t, 'errAmountMin1') : '';
    },
    submit: async (c, v) => {
      const amount = norm(v['amount'], 2);
      if (amount === null) throw localErr(route, str(t, 'errAmountMin1'));
      const alive = sessionGuard(c);
      await c.gp.api.call(route, { path: { id: b.id }, body: { amount } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'bgtToastSubmitted'));
      closeDrawer(c);
      await reloadCurrent(c);
    },
  };
}

function createForm(t: Dict): FormDef {
  return {
    title: str(t, 'bgtNew'),
    tip: str(t, 'bgtCreateTip'),
    fields: [
      { k: 'group_id', label: str(t, 'bgtCGroup'), type: 'number', placeholder: str(t, 'bgtGroupIdPh') },
      { k: 'name', label: str(t, 'bgtCName'), type: 'text' },
    ],
    init: { group_id: '', name: '' },
    validate: (v) => {
      // 正则只限形状；超出 2^53 的整数经 Number() 丢精度会发错卡组 ID
      const g = text(v['group_id']);
      if (!/^[1-9]\d{0,18}$/.test(g) || !Number.isSafeInteger(Number(g))) return str(t, 'bgtErrGroupId');
      const n = [...text(v['name'])].length;
      return n < 1 || n > 100 ? str(t, 'bgtErrName') : '';
    },
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      // 卡组已有 active 预算或在途建预算单 → 4220 field=group_id（err4220 插值字段名）
      await c.gp.api.call('POST /api/admin/budgets', { body: { group_id: Number(text(v['group_id'])), name: text(v['name']) } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'bgtToastSubmitted'));
      await reloadCurrent(c);
    },
  };
}

function rebaseForm(t: Dict, b: BudgetRow): FormDef {
  return {
    title: `${str(t, 'bgtRebase')} · ${b.name}`,
    tip: str(t, 'bgtRebaseTip'),
    fields: [{ k: 'note', label: str(t, 'bgtCNote'), type: 'text', wide: true }],
    init: { note: '' },
    validate: (v) => {
      const n = [...text(v['note'])].length;
      return n === 0 ? str(t, 'errNoteRequired') : n > 255 ? str(t, 'bgtErrNoteLong') : '';
    },
    // 1211 reason=ops_inflight / 1207 reason=rebase_below_committed：ui.err 括注 reason（TD-839）
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      await c.gp.api.call<BudgetRow>('POST /api/admin/budgets/{id}/rebase', { path: { id: b.id }, body: { note: text(v['note']) } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastSaved'));
      closeDrawer(c);
      await reloadCurrent(c);
    },
  };
}

function rebaseConfirm(c: AdminCtx, b: BudgetRow): void {
  const t = c.t;
  openConfirm(c, { title: str(t, 'bgtRebaseTitle'), text: str(t, 'bgtRebaseText'), label: b.name, run: async (x) => openForm(x, rebaseForm(t, b)) });
}

// 0044：只有本系统预算可处置；imported 外部预算只读（不处置、不统计），写按钮一律隐藏。
const isOwn = (b: BudgetRow): boolean => b.origin === 'own';
const writable = (b: BudgetRow): boolean => b.status === 'active' && isOwn(b);

const OUTCOME_LABEL: Readonly<Record<string, string>> = { success: 'bgtOutcomeSuccess', failed: 'bgtOutcomeFailed' };

function opResolveForm(t: Dict, op: BudgetOp): FormDef {
  const needLog = op.kind !== 'create'; // 建预算单成功由服务端实查卡组预算列表，不要求流水号
  return {
    title: `${str(t, 'aResolve')} · ${op.biz_no}`,
    tip: str(t, 'resolveTip'),
    fields: [
      // 06 C-27 结案 outcome 默认值「—」：首项空值占位，未选即按必填项拦截
      { k: 'outcome', label: str(t, 'cOutcome'), type: 'select', options: ['', 'success', 'failed'], optionLabel: (d, o) => (o === '' ? '—' : str(d, OUTCOME_LABEL[o] ?? '') || o) },
      { k: 'upstream_log_id', label: str(t, 'bgtCUpLogId'), type: 'text', optional: true, placeholder: str(t, 'bgtUpLogIdPh') },
      { k: 'reason', label: str(t, 'cReason'), type: 'text', wide: true },
    ],
    init: { outcome: '', upstream_log_id: '', reason: '' },
    validate: (v) => {
      const n = [...text(v['reason'])].length;
      if (n < 10 || n > 255) return str(t, 'errReason10');
      if (v['outcome'] !== 'success' && v['outcome'] !== 'failed') return str(t, 'required');
      if (needLog && v['outcome'] === 'success' && !text(v['upstream_log_id'])) return str(t, 'bgtErrUpLogId');
      return '';
    },
    submit: async (c, v) => {
      const log = text(v['upstream_log_id']);
      const alive = sessionGuard(c);
      try {
        await c.gp.api.call(RESOLVE_ROUTE, { path: { biz_no: op.biz_no }, body: { outcome: String(v['outcome']), reason: text(v['reason']), ...(log ? { upstream_log_id: log } : {}) } });
      } catch (e) {
        // 06 C-27：结案人 = 发起人 → 1501（表单内已有 approve 位，1501 即同人拦截）
        if (e instanceof ApiError && e.code === 1501) throw localErr(RESOLVE_ROUTE, str(t, 'errSameReviewer'));
        throw e;
      }
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastResolve'));
      closeDrawer(c);
      await reloadCurrent(c);
    },
  };
}

// ---- C-27 抽屉 ----

function operatorText(t: Dict, op: BudgetOp): string {
  if (op.operator === 'system') return str(t, 'bgtOpSystem');
  return op.operator_admin_id === null ? op.operator : `admin #${op.operator_admin_id}`;
}

function opRow(ctx: AdminCtx, op: BudgetOp): NonNullable<DrawerSpec['list']>['rows'][number] {
  const t = ctx.t;
  const kind = str(t, OP_KIND[op.kind] ?? '') || op.kind;
  const unknown = op.status === 'unknown';
  // 06 C-27 单据「时间」：created_at，sub = submitted_at / finalized_at
  const sub = [
    op.biz_no, operatorText(t, op), op.card_action_id !== null ? str(t, 'bgtOpByCard') : '', time(ctx, op.created_at),
    op.submitted_at === null ? '' : interpolate(str(t, 'bgtOpSubmittedAt'), { t: time(ctx, op.submitted_at) }),
    op.finalized_at === null ? '' : interpolate(str(t, 'bgtOpFinalizedAt'), { t: time(ctx, op.finalized_at) }),
  ];
  const tail = [
    op.fee_estimate === null ? '' : `${str(t, 'bgtFeeEstimate')} ${usd(op.fee_estimate)}`,
    op.upstream_task_id === null ? '' : `task ${op.upstream_task_id}`,
    op.upstream_log_id ?? '',
    unknown ? interpolate(str(t, 'bgtClaimRounds'), { n: op.claim_rounds }) : '',
    op.error_message ?? '',
    op.resolved_by === null ? '' : `${interpolate(str(t, 'bgtResolvedBy'), { id: op.resolved_by })} · ${time(ctx, op.resolved_at)}${op.resolve_note ? ` · ${op.resolve_note}` : ''}`,
    unknown ? `${str(t, 'aResolve')} ›` : '',
  ];
  // 06「错误」红字：li 只有整行 rightSub 一种色调，有 error_message 时整段取 bad（unknown 仍取 warn 以示可结案）
  const tone: Tone = unknown ? 'warn' : op.status === 'failed' || op.error_message ? 'bad' : op.status === 'success' ? 'good' : '';
  return {
    left: `${kind} · ${usd(op.amount)}`,
    sub: sub.filter(Boolean).join(' · '),
    right: chipOf(chipMap(t, OP_STATUS), op.status, 'budget_op.status').v,
    rightSub: tail.filter(Boolean).join(' · '),
    tone,
    // 06：status=unknown 的单据可人工结案（wallets.approve）；无该位只提示，不开表单
    ...(unknown
      ? {
          go: (c: AdminCtx) => {
            if (!can(c.s.sh.me, 'wallets', 'approve')) {
              c.gp.ui.toast(str(c.t, 'toastNoPerm'));
              return;
            }
            openForm(c, opResolveForm(c.t, op));
          },
        }
      : {}),
  };
}

function budgetDrawer(ctx: AdminCtx, b: BudgetRow, ops: readonly BudgetOp[]): DrawerSpec {
  const t = ctx.t;
  const status = chipOf(chipMap(t, BUDGET_STATUS), b.status, 'budget.status').v;
  const writes: readonly DrawerAction[] = writable(b)
    ? [
        { label: 'bgtTopup', kind: 'green', need: W_EDIT, run: (c) => openForm(c, amountForm(c.t, 'topup', b)) },
        { label: 'bgtWithdraw', kind: '', need: W_EDIT, run: (c) => openForm(c, amountForm(c.t, 'withdraw', b)) },
        { label: 'bgtRebase', kind: 'danger', need: W_APPROVE, run: (c) => rebaseConfirm(c, b) },
      ]
    : [];
  return {
    kind: str(t, 'budgets'),
    title: b.name,
    subtitle: `${str(t, 'bgtCGroup')} ${b.group_id} · ${str(t, 'upstreamWord')} ${b.upstream_budget_id}${isOwn(b) ? '' : ` · ${str(t, 'bgtImported')}`}`,
    // 06 C-27 抽屉字段格顺序
    fields: [
      { k: str(t, 'bgtCFunded'), v: usd(b.funded) },
      { k: str(t, 'bgtCCommitted'), v: usd(b.committed) },
      { k: str(t, 'bgtFReserved'), v: usd(b.reserved) },
      { k: str(t, 'bgtFTopupInflight'), v: usd(b.topup_inflight) },
      { k: str(t, 'bgtFWithdrawInflight'), v: usd(b.withdraw_inflight) },
      { k: str(t, 'bgtCSurplus'), v: usd(b.surplus) },
      { k: str(t, 'bgtCUpBalance'), v: usd(b.balance) },
      { k: str(t, 'bgtFAllocated'), v: usd(b.allocated) },
      { k: str(t, 'bgtFLocalLive'), v: String(b.local_live_cards) },
      { k: str(t, 'bgtFOpened'), v: String(b.opened_card_count) },
      { k: str(t, 'bgtFMaxCards'), v: String(b.card_max_count) },
      { k: str(t, 'bgtFAvailable'), v: String(b.available_card_count) },
      { k: str(t, 'cCurrency'), v: b.currency },
      { k: str(t, 'cStatus'), v: status },
      { k: str(t, 'bgtFOrigin'), v: isOwn(b) ? str(t, 'bgtOriginOwn') : str(t, 'bgtImported') },
      { k: str(t, 'bgtCSynced'), v: time(ctx, b.synced_at) },
    ],
    list: { title: str(t, 'bgtOps'), rows: ops.map((op) => opRow(ctx, op)) },
    actions: [...writes, { label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) }],
  };
}

// 先开抽屉（单据列表为空），再取第 1 页单据回填；会话已换、或当前抽屉已不是本次打开的那一个则丢弃
function openBudget(ctx: AdminCtx, b: BudgetRow): void {
  const spec = budgetDrawer(ctx, b, []);
  openDrawer(ctx, spec);
  const alive = sessionGuard(ctx);
  void ctx.gp.api
    .call<AdminPage<BudgetOp>>('GET /api/admin/budgets/{id}/ops', { path: { id: b.id }, query: { page: 1, size: 10 } })
    .then((p) => {
      if (!alive() || ctx.host.state.tb.drawer !== spec) return;
      openDrawer(ctx, budgetDrawer(ctx, b, Array.isArray(p.list) ? p.list : []));
    })
    .catch((e: unknown) => {
      if (alive() && ctx.host.state.tb.drawer === spec) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    });
}

function budgetCells(c: AdminCtx, b: BudgetRow): readonly CellVals[] {
  const t = c.t;
  const surplus = cell(usd(b.surplus));
  const s = micro(b.surplus);
  const inflightZero = micro(b.topup_inflight) === 0n && micro(b.withdraw_inflight) === 0n;
  // 0044：卡数主值 = 本地在册卡数；上游 opened / max / 可开为整预算计数（含外来卡），sub 标「上游计数，仅参考」
  const cards = cell(String(b.local_live_cards), interpolate(str(t, 'bgtCardsUpSub'), { opened: b.opened_card_count, max: b.card_max_count, n: b.available_card_count }));
  const upBal = cell(usd(b.balance), interpolate(str(t, 'bgtAllocatedSub'), { n: money(b.allocated) }));
  const head = `#${b.id} · ${str(t, 'upstreamWord')} ${b.upstream_budget_id}`;
  const name = cell(b.name, isOwn(b) ? head : `${head} · ${str(t, 'bgtImported')}`);
  return [
    isOwn(b) ? name : { ...name, style: `${name.style};${GRAY}` },
    cell(String(b.group_id), b.currency, true),
    cell(usd(b.funded)),
    cell(usd(b.committed)),
    cell(usd(b.reserved), inflightZero ? '' : interpolate(str(t, 'bgtInflightSub'), { topup: money(b.topup_inflight), withdraw: money(b.withdraw_inflight) })),
    s !== null && cmp(s, 0n) <= 0 ? { ...surplus, style: `${surplus.style};${RED}` } : surplus,
    { ...upBal, style: `${upBal.style};${GRAY}` },
    isOwn(b) && b.available_card_count <= 0 ? { ...cards, style: `${cards.style};${RED}` } : cards,
    chipOf(chipMap(t, BUDGET_STATUS), b.status, 'budget.status'),
    cell(time(c, b.synced_at)),
  ];
}

const budgetsView = defineView<BudgetRow>({
  key: 'all',
  source: { route: 'GET /api/admin/budgets', tabParam: 'none' },
  echo: '/admin/budgets',
  noDate: true, // 带 keyword / start_date / end_date → 4220（06 C-27 列表规格）
  noKeyword: true,
  cols: [
    { label: 'bgtCBudget', sortKey: 'id' }, { label: 'bgtCGroup' }, { label: 'bgtCFunded' }, { label: 'bgtCCommitted' }, { label: 'bgtCReservedInflight' },
    { label: 'bgtCSurplus' }, { label: 'bgtCUpBalance' }, { label: 'bgtCCardCount' }, { label: 'cStatus', facet: 'status', facetLabel: facetOf(BUDGET_STATUS) }, { label: 'bgtCSynced' },
  ],
  cells: budgetCells,
  open: openBudget,
  rowActions: () => [
    { label: 'aDetail', run: (c, b) => openBudget(c, b) },
    { label: 'bgtTopup', need: W_EDIT, show: writable, run: (c, b) => openForm(c, amountForm(c.t, 'topup', b)) },
    { label: 'bgtWithdraw', need: W_EDIT, show: writable, run: (c, b) => openForm(c, amountForm(c.t, 'withdraw', b)) },
    { label: 'bgtRebase', need: W_APPROVE, danger: true, show: writable, run: (c, b) => rebaseConfirm(c, b) },
  ],
  stats: coverageStats,
  // C-27 双主操作（D31）：+ 建预算（primary）与 ↻ 同步预算（secondary）
  primary: { label: 'bgtNew', icon: '+ ', need: W_EDIT, run: (c) => openForm(c, createForm(c.t)) },
  secondary: {
    label: 'bgtSync',
    icon: '↻ ',
    need: W_EDIT,
    run: async (c) => {
      const alive = sessionGuard(c);
      const r = await c.gp.api.call<SyncResult>('POST /api/admin/budgets/sync', { body: {} });
      if (!alive()) return; // 会话已换：同步结果不提示、不刷新
      c.gp.ui.toast(interpolate(str(c.t, 'bgtToastSync'), { imported: r.imported, updated: r.updated, ignored: r.ignored ?? 0, n: Array.isArray(r.skipped) ? r.skipped.length : 0 }));
      await reloadCurrent(c);
    },
  },
  csvName: 'budgets',
});

// ---- C-30 成本台账 ----

const costLabel = (t: Dict, x: UpstreamCost): string => chipOf(chipMap(t, COST_TYPE), x.type, 'upstream_cost.type').v;
const last4 = (x: UpstreamCost): string => (x.card_last4 !== null && /^\d{4}$/.test(x.card_last4) ? `•• ${x.card_last4}` : '—');
const idOr = (n: number | null, prefix = ''): string => (n === null ? '—' : `${prefix}${n}`);

function costDrawer(ctx: AdminCtx, x: UpstreamCost): DrawerSpec {
  const t = ctx.t;
  return {
    kind: str(t, 'upstreamCosts'),
    title: `${costLabel(t, x)} · ${usd(x.amount)}`,
    // 06 C-30：detail 出口清洗失败整段为 null → 不渲染 JSON 区，副题提示「明细不可展示」
    subtitle: x.detail === null ? str(t, 'bgtCostNoDetail') : x.upstream_log_id,
    fields: [
      { k: str(t, 'cType'), v: costLabel(t, x) },
      { k: str(t, 'cAmount'), v: usd(x.amount) },
      { k: str(t, 'cCurrency'), v: x.currency },
      { k: str(t, 'bgtCBudget'), v: idOr(x.budget_id, '#') },
      { k: str(t, 'bgtFUpBudgetId'), v: idOr(x.upstream_budget_id) },
      { k: str(t, 'bgtFBudgetOp'), v: idOr(x.budget_op_id, '#') },
      { k: str(t, 'bgtCCard'), v: last4(x) },
      { k: str(t, 'bgtFUpCardId'), v: idOr(x.upstream_card_id) },
      { k: str(t, 'bgtFCardId'), v: idOr(x.card_id) },
      { k: str(t, 'bgtFCardActionId'), v: idOr(x.card_action_id) },
      { k: str(t, 'bgtFLoggedAt'), v: time(ctx, x.logged_at) },
      { k: str(t, 'bgtFCreatedAt'), v: time(ctx, x.created_at) },
    ],
    ...(x.detail === null ? {} : { json: JSON.stringify(x.detail, null, 2), jsonTitle: str(t, 'bgtDetailTitle') }),
    actions: [{ label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) }],
  };
}

const openCost = (c: AdminCtx, x: UpstreamCost): void => openDrawer(c, costDrawer(c, x));

const upstreamCostsView = defineView<UpstreamCost>({
  key: 'all',
  source: { route: 'GET /api/admin/upstream-costs', tabParam: 'none' },
  echo: '/admin/upstream-costs',
  cols: [
    { label: 'cTime', sortKey: 'logged_at' }, { label: 'cType', facet: 'type', facetLabel: facetOf(COST_TYPE) }, { label: 'cAmount' },
    { label: 'bgtCBudget' }, { label: 'bgtCCard' }, { label: 'bgtCLogId' },
  ],
  cells: (c, x) => {
    const t = c.t;
    const budgetSub = [x.upstream_budget_id === null ? '' : `${str(t, 'upstreamWord')} ${x.upstream_budget_id}`, x.budget_op_id === null ? '' : interpolate(str(t, 'bgtOpRef'), { id: x.budget_op_id })];
    return [
      cell(time(c, x.logged_at), time(c, x.created_at)),
      chipOf(chipMap(t, COST_TYPE), x.type, 'upstream_cost.type'),
      cell(usd(x.amount), x.currency),
      cell(idOr(x.budget_id, '#'), budgetSub.filter(Boolean).join(' · '), true),
      cell(last4(x), x.upstream_card_id === null ? '' : String(x.upstream_card_id), true),
      cell(x.upstream_log_id, '', true),
    ];
  },
  open: openCost,
  rowActions: () => [
    { label: 'aDetail', run: openCost },
    // 06 C-30：本地卡 id 非空时可开 C-05 卡抽屉
    { label: 'bgtOpenCard', show: (x) => x.card_id !== null, run: (c, x) => (x.card_id === null ? undefined : openCardDrawer(c, x.card_id)) },
  ],
  stats: coverageStats,
  csvName: 'upstream-costs',
});

// ---- C-25 金库归集（2026-10-15 已下线：perm.RETIRED_PAGES 隐藏本页，登记接口不再注册；视图只读保留以便回溯） ----

function sweepDrawer(ctx: AdminCtx, s: Sweep): DrawerSpec {
  const t = ctx.t;
  return {
    kind: str(t, 'treasury'),
    title: `${money(s.amount)} USDT · ${s.chain}`,
    subtitle: `${s.operator.account} · ${time(ctx, s.created_at)}`,
    fields: [
      { k: str(t, 'bgtCChain'), v: s.chain },
      { k: str(t, 'cAmount'), v: `${money(s.amount, 6)} USDT` },
      { k: str(t, 'bgtCFromAddr'), v: s.from_address, wide: true },
      { k: str(t, 'bgtCToAddr'), v: s.to_address, wide: true },
      { k: str(t, 'cTxHash'), v: s.tx_hash, wide: true },
      { k: str(t, 'bgtCOperator'), v: s.operator.account },
      { k: str(t, 'cTime'), v: time(ctx, s.created_at) },
      { k: str(t, 'bgtCNote'), v: s.note || '—', wide: true },
    ],
    actions: [{ label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) }],
  };
}

const openSweep = (c: AdminCtx, s: Sweep): void => openDrawer(c, sweepDrawer(c, s));

// 06 C-25 统计卡数据源 = 列表响应顶部 summary（F-14b / F-14c / 本月归集）。后端只在 page=1 的 JSON 请求计算，其余页为 null：
// 翻页沿用本会话最近一次 page=1 的 summary（会话换代即失效）；page=1 为 null（dashq 未装配）或形状非法时三卡显示「—」
function isSummary(x: unknown): x is TreasurySummary {
  if (typeof x !== 'object' || x === null || !('liability' in x) || !('net_inflow' in x) || !('sweep_month' in x)) return false;
  const m = x.sweep_month;
  return typeof x.liability === 'string' && typeof x.net_inflow === 'string' && typeof m === 'object' && m !== null && 'amount' in m && 'count' in m && typeof m.amount === 'string' && typeof m.count === 'number';
}

const summaryCache = new WeakMap<object, { alive: () => boolean; s: TreasurySummary | null }>();

function treasurySummary(ctx: AdminCtx, page: Readonly<Record<string, unknown>>): TreasurySummary | null {
  if (page['page'] === 1) {
    const s = isSummary(page['summary']) ? page['summary'] : null;
    summaryCache.set(ctx.host, { alive: sessionGuard(ctx), s });
    return s;
  }
  const hit = summaryCache.get(ctx.host);
  return hit?.alive() ? hit.s : null;
}

function treasuryStats(ctx: AdminCtx, page: Readonly<Record<string, unknown>>): readonly StatVals[] {
  const t = ctx.t;
  const s = treasurySummary(ctx, page);
  return [
    { label: str(t, 'bgtKLiability'), value: s ? signedUsd(s.liability) : '—', sub: str(t, 'bgtKLiabilitySub'), dark: true },
    { label: str(t, 'bgtKNetInflow'), value: s ? signedUsd(s.net_inflow) : '—', sub: str(t, 'bgtKNetInflowSub') },
    { label: str(t, 'bgtKSweepMonth'), value: s ? `${money(s.sweep_month.amount)} USDT` : '—', sub: s ? interpolate(str(t, 'bgtSweepCount'), { n: s.sweep_month.count }) : '' },
  ];
}

const treasuryView = defineView<Sweep>({
  key: 'all',
  source: { route: 'GET /api/admin/treasury/sweeps', tabParam: 'none' },
  echo: '/admin/treasury/sweeps',
  cols: [
    { label: 'cTime', sortKey: 'created_at' }, { label: 'bgtCChain', facet: 'chain' }, { label: 'bgtCFromAddr' }, { label: 'bgtCToAddr' },
    { label: 'cAmount' }, { label: 'cTxHash' }, { label: 'bgtCOperator' }, { label: 'bgtCNote' },
  ],
  cells: (c, s) => [
    cell(time(c, s.created_at)),
    cell(s.chain),
    cell(abbr(s.from_address, 4, 4), '', true),
    cell(abbr(s.to_address, 4, 4), '', true),
    cell(`${money(s.amount)} USDT`),
    cell(abbr(s.tx_hash, 6, 4), '', true),
    cell(s.operator.account),
    cell(s.note ? clip(s.note, 24) : '—'),
  ],
  open: openSweep,
  rowActions: () => [{ label: 'aDetail', run: openSweep }],
  stats: treasuryStats,
  csvName: 'treasury-sweeps',
});

// TD-849：三页共享同一文案包对象（mergePagePacks 按引用去重）
const PACK = { i18n: I18N_BUDGETS, rewrites: REWRITES_BUDGETS } as const;

export const PAGES_BUDGETS: PageMap = {
  budgets: { page: 'budgets', ...PACK, tabLabels: (t) => [str(t, 'all')], views: [budgetsView] },
  upstreamCosts: { page: 'upstreamCosts', ...PACK, tabLabels: (t) => [str(t, 'all')], views: [upstreamCostsView] },
  treasury: { page: 'treasury', ...PACK, tabLabels: (t) => [str(t, 'all')], views: [treasuryView] },
};
